import { useEffect, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { appNow } from "@/lib/date-utils";
import {
  isSessionInactive,
  parseSessionActivity,
  serializeSessionActivity,
  SESSION_ACTIVITY_STORAGE_KEY,
  SESSION_INACTIVITY_MS,
} from "@/lib/session-inactivity";

const ACTIVITY_WRITE_THROTTLE_MS = 5_000;

export function SessionInactivityGuard() {
  const { user, signOut } = useAuth();
  const signOutRef = useRef(signOut);

  useEffect(() => {
    signOutRef.current = signOut;
  }, [signOut]);

  useEffect(() => {
    const userId = user?.id;
    if (!userId) {
      localStorage.removeItem(SESSION_ACTIVITY_STORAGE_KEY);
      return;
    }

    let timerId = 0;
    let expiring = false;
    const stored = parseSessionActivity(localStorage.getItem(SESSION_ACTIVITY_STORAGE_KEY));
    let lastActivity = stored?.userId === userId ? stored.at : appNow().getTime();
    let lastPersistedAt = stored?.userId === userId ? stored.at : 0;

    const persist = (at: number) => {
      localStorage.setItem(SESSION_ACTIVITY_STORAGE_KEY, serializeSessionActivity({ userId, at }));
      lastPersistedAt = at;
    };

    const expire = () => {
      if (expiring) return;
      expiring = true;
      window.clearTimeout(timerId);
      localStorage.removeItem(SESSION_ACTIVITY_STORAGE_KEY);
      void signOutRef.current().finally(() => {
        window.location.replace("/auth?reason=inactivity");
      });
    };

    const schedule = () => {
      window.clearTimeout(timerId);
      const now = appNow().getTime();
      if (isSessionInactive(lastActivity, now)) {
        expire();
        return;
      }
      timerId = window.setTimeout(expire, Math.max(1, lastActivity + SESSION_INACTIVITY_MS - now));
    };

    const recordActivity = () => {
      if (expiring) return;
      const now = appNow().getTime();
      lastActivity = now;
      if (now - lastPersistedAt >= ACTIVITY_WRITE_THROTTLE_MS) persist(now);
      schedule();
    };

    if (isSessionInactive(lastActivity, appNow().getTime())) {
      expire();
      return () => window.clearTimeout(timerId);
    }
    if (!stored || stored.userId !== userId) persist(lastActivity);
    schedule();

    const activityEvents: Array<keyof WindowEventMap> = ["pointerdown", "pointermove", "keydown", "scroll", "touchstart"];
    activityEvents.forEach((event) => window.addEventListener(event, recordActivity, { passive: true }));

    const onStorage = (event: StorageEvent) => {
      if (event.key !== SESSION_ACTIVITY_STORAGE_KEY) return;
      const shared = parseSessionActivity(event.newValue);
      if (shared?.userId !== userId || shared.at <= lastActivity) return;
      lastActivity = shared.at;
      lastPersistedAt = shared.at;
      schedule();
    };
    const onVisibilityChange = () => {
      if (document.visibilityState !== "visible") return;
      if (isSessionInactive(lastActivity, appNow().getTime())) expire();
      else recordActivity();
    };
    window.addEventListener("storage", onStorage);
    document.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      window.clearTimeout(timerId);
      activityEvents.forEach((event) => window.removeEventListener(event, recordActivity));
      window.removeEventListener("storage", onStorage);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [user?.id]);

  return null;
}
